import React, { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CgAdd, CgTrash, CgPen } from 'react-icons/cg'
import { FiMove } from 'react-icons/fi'
import tw from 'twin.macro'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useEventListener } from 'common/hooks/useEventListener'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import {
  useListenProductCurrencyQuery,
  useListenStripeProductIdQuery,
  useListenStripeResponseQuery,
  useSetProductPricesMutation,
  useSetStripeRequestMutation,
} from 'modules/ProductInfo/productInfoApi'

import { closestCenter, DndContext, DragOverlay, MouseSensor, TouchSensor, useSensors, useSensor } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { Sortable } from 'common/components/Sortable/Sortable'
import { dropAnimation } from 'common/utils/dndUtils'

import { PRICING } from 'modules/App/components/Header'
import {
  getDefaultNextPlan,
  getPlanPromoIntervalText,
  getPlanIntervalText,
  getPromoText,
  getTrialText,
  getProductCurrency,
  getAppliedPromo,
  getPromoPrice,
} from 'modules/ProductInfo/utils/utils'
import { CURRENCY_SYMBOL } from 'common/constants/currencyConstants'
import { Spinner } from 'common/components/Spinner/Spinner'
import { EmptyStateContainer } from 'common/components/EmptyStateContainer/EmptyStateContainer'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { createUID } from 'common/utils/createUID'
import { PricingForm } from '../PricingForm/PricingForm'

export default function PricingList({ coachOrgId, coupons, plans, loading, isMob }) {
  const { pathname } = useLocation()

  const [editingPrice, setEditingPrice] = useState(null)
  const [addPlanDialogOpen, setAddPlanDialogOpen] = useState(false)

  const [setPrices] = useSetProductPricesMutation()

  const { data: currencyData } = useListenProductCurrencyQuery({ coachOrgId })

  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading

  const deselectOnEsc = (e) => {
    if (e.code === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      setEditingPrice(null)
    }
  }

  useEventListener('keydown', deselectOnEsc)

  // Drag and drop
  const [activeDragItem, setActiveDragItem] = useState(null)
  const dragItemAppliedPromo = getAppliedPromo({ plan: activeDragItem?.plan, coupons })
  const dragItemAppliedCoupon = coupons?.[dragItemAppliedPromo?.stripeCouponId]

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const handleDragStart = ({ active }) => {
    setEditingPrice(null)
    const activePlan = plans.find((plan) => plan.id === active.id)
    setActiveDragItem({ plan: activePlan })
  }

  const handleDragEnd = async ({ active, over }) => {
    if (active.id !== over.id) {
      const oldIndex = plans?.findIndex((plan) => plan.id === active.id)
      const newIndex = plans?.findIndex((plan) => plan.id === over.id)

      const movedPlans = arrayMove(plans, oldIndex, newIndex).map((plan, newIdx) => ({ ...plan, idx: newIdx }))
      let updatedPlans = {}
      movedPlans.forEach((plan) => {
        updatedPlans[plan.id] = plan
      })
      await setPrices({ coachOrgId, plans: updatedPlans })
      setActiveDragItem(null)
    }
  }

  const planIds = plans?.map((plan) => plan?.id) || []

  if (loading || stripeProductIdLoading) {
    return (
      <div className='flex justify-center items-center h-52'>
        <Spinner className='w-10 h-10 text-gray-100' />
      </div>
    )
  }

  const defaultNextPlan = getDefaultNextPlan(plans)
  if (plans?.length === 0) {
    return (
      <>
        <EmptyStateContainer
          text='No pricing plans'
          action={() => setAddPlanDialogOpen(true)}
          actionText='Add new plan'
          containerClasses='!h-52 border-2 border-gray-200'
          textCss={tw`text-2xl`}
        />
        <Dialog open={addPlanDialogOpen} setOpen={setAddPlanDialogOpen}>
          <DialogContent header='Add plan' overlayProps={{ 'data-no-dnd': true }} contentClassNames='!w-[700px]'>
            <FormRefsControlProvider>
              <PricingForm
                coachOrgId={coachOrgId}
                plan={defaultNextPlan}
                plans={plans}
                closeForm={() => setAddPlanDialogOpen(false)}
                isCreateForm={true}
              />
            </FormRefsControlProvider>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <div className='flex flex-col border-2 border-gray-200 rounded-xl overflow-hidden'>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={planIds} strategy={verticalListSortingStrategy}>
          {plans.map((plan, idx) => {
            const appliedPromo = getAppliedPromo({ product: plan, coupons })
            const appliedCoupon = coupons?.[appliedPromo?.stripeCouponId]
            return (
              <Sortable
                key={plan.id}
                id={plan.id}
                withHandle={true}
                css={[
                  tw`flex flex-col border-b-[1px] border-b-gray-300 last:border-b-0`,
                  plan.idx === plans.length - 1 && tw`border-b-0`,
                ]}
                draggingClasses='ring-2 ring-tGreen opacity-50'
              >
                <PricingListItem
                  coachOrgId={coachOrgId}
                  plans={plans}
                  plan={plan}
                  editingPrice={editingPrice}
                  setEditingPrice={setEditingPrice}
                  appliedCoupon={appliedCoupon}
                  currency={getProductCurrency({ productCurrency: plan?.currency, defaultCurrency: currencyData })}
                  isMob={isMob}
                />
              </Sortable>
            )
          })}
        </SortableContext>
        <DragOverlay
          zIndex={10}
          className='cursor-move'
          dropAnimation={dropAnimation}
          modifiers={[restrictToVerticalAxis, restrictToParentElement, restrictToFirstScrollableAncestor]}
        >
          {activeDragItem && (
            <PricingListItem
              coachOrgId={coachOrgId}
              plans={plans}
              plan={activeDragItem.plan}
              editingPrice={editingPrice}
              appliedCoupon={dragItemAppliedCoupon}
              setEditingPrice={setEditingPrice}
            />
          )}
        </DragOverlay>
      </DndContext>
      <Dialog open={addPlanDialogOpen} setOpen={setAddPlanDialogOpen}>
        {plans.length < 4 && (
          <DialogTrigger
            css={[
              tw`flex items-center justify-center p-4 min-h-[56px] hover:bg-gray-100 cursor-pointer font-bold border-t-[1px] border-t-tGray-light`,
              pathname.includes(PRICING) && tw`px-8 py-6`,
            ]}
            onOpenCb={(e) => e.stopPropagation()}
          >
            <CgAdd className='w-5 h-5 mr-2' /> Add plan
          </DialogTrigger>
        )}
        <DialogContent header='Add plan' overlayProps={{ 'data-no-dnd': true }} contentClassNames='!w-[700px]'>
          <FormRefsControlProvider>
            <PricingForm
              coachOrgId={coachOrgId}
              plan={defaultNextPlan}
              plans={plans}
              closeForm={() => {
                setAddPlanDialogOpen(false)
                setEditingPrice(null)
              }}
              isCreateForm={true}
            />
          </FormRefsControlProvider>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const PricingListItem = React.memo(
  ({
    coachOrgId,
    plans,
    plan,
    editingPrice,
    setEditingPrice,
    appliedCoupon,
    currency,
    isMob,
    attributes,
    listeners,
  }) => {
    const { pathname } = useLocation()
    const { createAlert } = useAlert()

    const actionContainerRef = useRef(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const [setStripeRequest] = useSetStripeRequestMutation()
    const [setPrices] = useSetProductPricesMutation()

    const { data: stripeResponse } = useListenStripeResponseQuery({ coachOrgId, responseId: plan?.id })
    const stripeResponsePending = stripeResponse?.status === 'pending'
    const errorMessage = stripeResponse?.error

    const promoText = getPromoText({ activeCoupon: appliedCoupon, plan, currency })
    const trialText = getTrialText({ activeCoupon: appliedCoupon, plan })

    const handleDelete = async () => {
      const payload = {
        trybeProductId: plan?.id,
        stripePriceId: plan?.stripe_price_id || plan?.stripe_plan_id || null,
      }
      const requestId = createUID()
      const { error } = await setStripeRequest({
        requestId,
        coachOrgId,
        type: 'DELETE_PRICE',
        payload,
      })

      if (error) {
        createAlert({ text: 'Could not delete the plan. Please try again.', type: 'danger' })
        return
      }

      createAlert({ text: 'Plan deleted!', type: 'success' })
    }

    const handleErrRetry = async () => {
      const payload = stripeResponse?.data?.payload
      const type = stripeResponse?.type
      const requestId = createUID()
      await setStripeRequest({
        requestId,
        coachOrgId,
        type,
        payload,
      })
      createAlert({ text: 'Retrying...', type: 'success' })
    }

    const handleErrDelete = async () => {
      const filteredPlans = plans.filter((pl) => pl.id !== plan.id)
      let updatedPlans = {}
      filteredPlans.forEach((pl, index) => {
        updatedPlans[pl.id] = { ...pl, idx: index }
      })
      await setPrices({ coachOrgId, plans: updatedPlans })
      createAlert({ text: 'Plan deleted!', type: 'success' })
    }

    const handlePriceRowClick = (e) => {
      // Check that event did not happen in modal
      const occuredOutsideModal = e.currentTarget.contains(e.target)

      // Check that event did not happen in action row (edit/delete/drag).
      const occuredOutsideActionRow = !actionContainerRef.current?.contains(e.target)
      if (occuredOutsideModal && occuredOutsideActionRow) {
        setEditingPrice(plan.idx)
      }
    }

    return (
      <div>
        <div
          className='group'
          css={[
            tw`relative flex flex-col justify-center p-4 min-h-[56px] bg-white hover:bg-gray-50 cursor-pointer`,
            pathname.includes(PRICING) && tw`px-8 py-6`,
          ]}
          onClick={handlePriceRowClick}
        >
          <div className={`relative flex ${isMob ? 'flex-col items-start' : 'flex-row items-center'}`}>
            <div className='flex items-center'>
              <div className='relative flex items-center'>
                <span className='text-xl font-bold'>
                  {CURRENCY_SYMBOL[currency]}
                  {(plan.price / 100).toFixed(2)}{' '}
                  {!appliedCoupon || appliedCoupon?.type === 'trialOnly' ? currency : ''}
                </span>
                {appliedCoupon && appliedCoupon?.type !== 'trialOnly' && <DiscountLineThrough />}
              </div>
              {appliedCoupon && appliedCoupon?.type !== 'trialOnly' && (
                <span className='text-xl font-bold ml-1.5'>
                  {CURRENCY_SYMBOL[currency]}
                  {(getPromoPrice({ coupon: appliedCoupon, price: plan?.price }) / 100).toFixed(2)} {currency}
                </span>
              )}
            </div>
            <span className={`${isMob ? 'ml-0' : 'ml-1.5'}`}>
              {appliedCoupon && appliedCoupon?.type !== 'trialOnly'
                ? getPlanPromoIntervalText(plan)
                : getPlanIntervalText(plan)}
            </span>
          </div>
          <span className='text-xs font-medium'>{promoText}</span>
          <span className='text-xs font-medium'>{trialText}</span>
          {!errorMessage && (
            <div
              ref={actionContainerRef}
              className='hidden group-hover:flex absolute right-4 top-1/2 -translate-y-1/2 items-center py-1 px-1.5 rounded-md border-[1px] bg-white border-gray-200 shadow-sm'
            >
              {stripeResponsePending ? (
                <Spinner />
              ) : (
                <>
                  <button
                    type='button'
                    className='text-gray-500 hover:text-tGreen transition-all'
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingPrice(plan.idx)
                    }}
                  >
                    <CgPen className='w-[18px] h-[18px]' />
                  </button>
                  <button
                    type='button'
                    className='text-gray-500 hover:text-tRed transition-all disabled:cursor-not-allowed ml-2'
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteDialogOpen(true)
                    }}
                  >
                    <CgTrash className='w-5 h-5' />
                  </button>
                  {plans.length > 1 && (
                    <button
                      {...attributes}
                      {...listeners}
                      tabIndex={-1}
                      className='ml-2 cursor-move text-gray-500 hover:text-tGreen'
                    >
                      <FiMove className='w-[18px] h-[18px]' />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Both dialogs are deliberately outside the row's action buttons above.
            Those buttons are swapped for a spinner while a Stripe request for this plan is
            in flight, and hidden entirely once one comes back with an error. While the
            dialogs lived among them, either of those changes unmounted an open dialog with
            them: a coach editing a plan had the form disappear the moment the backend
            picked up a request for it — which happens seconds after creating a plan — and
            it came back empty afterwards, because the open state lives out here while the
            form's values do not. If the request failed instead, the form did not come back
            at all. Out here, the buttons can come and go without touching an open form. */}
        <Dialog open={editingPrice === plan.idx} setOpen={(open) => setEditingPrice(open ? plan.idx : null)}>
          <DialogContent
            header='Edit plan'
            overlayProps={{ 'data-no-dnd': true }}
            contentClassNames='!w-[700px] !max-h-[calc(100vh-250px)]'
          >
            <FormRefsControlProvider>
              <PricingForm
                coachOrgId={coachOrgId}
                plan={plan}
                plans={plans}
                closeForm={() => setEditingPrice(null)}
                handleDelete={() => handleDelete()}
              />
            </FormRefsControlProvider>
          </DialogContent>
        </Dialog>
        <Dialog open={deleteDialogOpen} setOpen={setDeleteDialogOpen}>
          <DialogContent>
            <DeleteDialogBanner text='This will delete the pricing plan' handleDelete={() => handleDelete()} />
          </DialogContent>
        </Dialog>
        {Boolean(errorMessage) && (
          <div className='text-xs mt-1 max-w-[90%] px-4 py-1'>
            <span className='text-tRed'>{errorMessage}</span>
            <button
              type='button'
              className='ml-2 underline hover:text-tGreen transition-colors'
              onClick={(e) => {
                e.stopPropagation()
                handleErrRetry()
              }}
            >
              Retry
            </button>
            <span className='mx-2'>or</span>
            <button
              type='button'
              className='underline hover:text-tRed transition-colors'
              onClick={(e) => {
                e.stopPropagation()
                handleErrDelete()
              }}
            >
              Delete plan
            </button>
          </div>
        )}
      </div>
    )
  }
)

const DiscountLineThrough = tw.div`absolute w-full h-[2px] bg-tRed`
